import { ArrowRight, Clock3 } from "lucide-react";
import type { DemoCardConfig } from "./demoHubConfig";
import { cn } from "@/lib/utils";

type DemoCardProps = {
  demo: DemoCardConfig;
  onPlaceholderClick: (title: string) => void;
};

const statusClasses: Record<DemoCardConfig["status"], string> = {
  "Live demo": "border-emerald-200 bg-emerald-50 text-emerald-700",
  "Available soon": "border-sky-200 bg-sky-50 text-sky-700",
  "Coming soon": "border-slate-200 bg-slate-50 text-slate-600",
};

export function DemoCard({ demo, onPlaceholderClick }: DemoCardProps) {
  const Icon = demo.icon;
  const isLive = Boolean(demo.url);

  const content = (
    <>
      <div className="flex items-start justify-between gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center border border-slate-200 bg-slate-50 text-slate-800">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </div>
        <span
          className={cn(
            "rounded-full border px-2.5 py-1 text-xs font-semibold",
            statusClasses[demo.status]
          )}
        >
          {demo.status}
        </span>
      </div>

      <div className="mt-6">
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
          {demo.persona}
        </p>
        <h3 className="mt-3 font-space-grotesk text-2xl font-semibold leading-tight text-slate-950">
          {demo.title}
        </h3>
        <p className="mt-4 min-h-[72px] text-sm leading-6 text-slate-600">{demo.description}</p>
      </div>

      <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-5">
        <span className="inline-flex items-center gap-2 text-sm font-medium text-slate-500">
          <Clock3 className="h-4 w-4" aria-hidden="true" />
          {demo.time}
        </span>
        <span
          className={cn(
            "inline-flex items-center gap-2 text-sm font-bold",
            isLive ? "text-slate-950" : "text-slate-500"
          )}
        >
          {isLive ? demo.ctaLabel : "Coming soon"}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </span>
      </div>
    </>
  );

  if (demo.url) {
    return (
      <a
        href={demo.url}
        className="group flex h-full flex-col border border-slate-200 bg-white p-5 transition-colors hover:border-slate-400 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400"
        data-demo-cta={demo.id}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      className="group flex h-full w-full flex-col border border-slate-200 bg-white p-5 text-left transition-colors hover:border-slate-300 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400"
      onClick={() => onPlaceholderClick(demo.title)}
      data-demo-cta={demo.id}
    >
      {content}
    </button>
  );
}
